import HttpClientFactory from "./HttpClientFactory";
import { HttpClient } from "@/details/data/backend/rest/http/HttpClient";
import { FetchHttpRequest, FetchRequestOptions } from "http-repository-cache";
import type { RepositoryRegistry } from "./RepositoryRegistry";

// Import all repository implementations

import CustomersRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerRepositoryBackend/CustomerRepositoryBackend";
import CustomerProfilRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerProfilRepositoryBackend/CustomerProfilRepositoryBackend";
import ClientRepositoryBackend from "@/details/data/backend/rest/repositories/ClientRepositoryBackend/ClientRepositoryBackend";
import RequestsRepositoryBackend from "@/details/data/backend/rest/repositories/RequestsRepositoryBackend/RequestsRepositoryBackend";
import ScreeningRepositoryBackend from "@/details/data/backend/rest/repositories/Documents/ScreeningRepositoryBackend/ScreeningRepositoryBackend";
import ProspectsRepositoryBackend from "@/details/data/backend/rest/repositories/ProspectsRepositoryBackend/ProspectsRepositoryBackend";
import UsersRepositoryBackend from "@/details/data/backend/rest/repositories/UserRepositoryBackend/UserRepositoryBackend";
import OTPRepositoryBackend from "@/details/data/backend/rest/repositories/OTPRepositoryBackend/OTPRepositoryBackend";
import ContactVerificationRepositoryBackend from "@/details/data/backend/rest/repositories/ContactVerificationRepositoryBackend/ContactVerificationRepositoryBackend";
import IdentityRepositoryBackend from "@/details/data/backend/rest/repositories/IdentityRepositoryBackend/IdentityRepositoryBackend";
import DocumentUploadRepositoryBackend from "@/details/data/backend/rest/repositories/Documents/DocumentUploadRepositoryBackend/DocumentUploadRepositoryBackend";
import CustomerAnalysisRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerAnalysisRepositoryBackend/CustomerAnalysisRepositoryBackend";
import ParametersRepositoryBackend from "@/details/data/backend/rest/repositories/ParametersRepositoryBackend/ParametersRepositoryBackend";
import LocalStorageLanguageRepository from "@/details/storage/localStorage/repositories/localStorageLanguageRepository/LocalStorageLanguageRepository";
import LocalStorageNavCollapsedRepository from "@/details/storage/localStorage/repositories/localStorageNavCollapsedRepository/LocalStorageNavCollapsedRepository";

import MetadataRepositoryBackend from "@/details/data/backend/rest/repositories/MetadataRepositoryBackend/MetadataRepositoryBackend";
import ProductsAccountsRepositoryBackend from "@/details/data/backend/rest/repositories/productsAccountsRepositoryBackend/productsAccountsRepositoryBackend";
import RequiredDocumentsRepositoryBackend from "@/details/data/backend/rest/repositories/RequiredDocumentsRepositoryBackend/RequiredDocumentsRepositoryBackend";
import CustomerDocumentsRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerDocumentsRepositoryBackend/CustomerDocumentsRepositoryBackend";
import ScreeningReportsRepositoryBackend from "@/details/data/backend/rest/repositories/ScreeningReportsRepositoryBackend/ScreeningReportsRepositoryBackend";
import AccountsRepositoryBackend from "@/details/data/backend/rest/repositories/AccountsRepositoryBackend/AccountsRepositoryBackend";
import MiniStatementRepositoryBackend from "@/details/data/backend/rest/repositories/MiniStatementRepositoryBackend/MiniStatementRepositoryBackend";

import RolesRepositoryBackend from "@/details/data/backend/rest/repositories/RolesRepositoryBackend/RolesRepositoryBackend";
import DashboardMetricsRepositoryBackend from "@/details/data/backend/rest/repositories/DashboardMetricsRepositoryBackend/DashboardMetricsRepositoryBackend";
import CustomerKYCRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerKYCRepositoryBackend/CustomerKYCRepositoryBackend";
import FakeRulesRepositoryBackend from "@/details/data/backend/rest/mock/FakeRulesRepositoryBackend";
import RiskEntityReferentialRepositoryBackend from "@/details/data/backend/rest/repositories/RiskEntityReferentialRepositoryBackend/RiskEntityReferentialRepositoryBackend";
import AgencesRepositoryBackend from "@/details/data/backend/rest/repositories/AgencesRepositoryBackend/AgencesRepositoryBackend";
import FakeL1ReviewRepositoryBackend from "@/details/data/backend/rest/mock/FakeL1ReviewRepositoryBackend";
import FakeP4ValidationRepositoryBackend from "@/details/data/backend/rest/mock/FakeP4ValidationRepositoryBackend";
import FakeCompositeVerdictRepositoryBackend from "@/details/data/backend/rest/mock/FakeCompositeVerdictRepositoryBackend";
import CompositeVerdictRepositoryBackend from "@/details/data/backend/rest/repositories/CompositeVerdictRepositoryBackend/CompositeVerdictRepositoryBackend";
import { isComplianceMockEnabled, isScoringMockEnabled } from "@/core/env/mockFlags";
import ScoringExplainRepositoryBackend from "@/details/data/backend/rest/repositories/ScoringExplainRepositoryBackend/ScoringExplainRepositoryBackend";
import FakeScoringExplainRepositoryBackend from "@/details/data/backend/rest/mock/FakeScoringExplainRepositoryBackend";
import EddRepositoryBackend from "@/details/data/backend/rest/repositories/EddRepositoryBackend/EddRepositoryBackend";
import FakeEddRepositoryBackend from "@/details/data/backend/rest/mock/FakeEddRepositoryBackend";
import RiskScoringCompositorRepositoryBackend from "@/details/data/backend/rest/repositories/RiskScoringCompositorRepositoryBackend/RiskScoringCompositorRepositoryBackend";
import PermissionsRepositoryBackend from "@/details/data/backend/rest/repositories/PermissionsRepositoryBackend/PermissionsRepositoryBackend";
import RoleOwnerRepositoryBackend from "@/details/data/backend/rest/repositories/RoleOwnerRepositoryBackend/RoleOwnerRepositoryBackend";
import RBACMatrixRepositoryBackend from "@/details/data/backend/rest/repositories/RBACMatrixRepositoryBackend/RBACMatrixRepositoryBackend";
import SoDRepositoryBackend from "@/details/data/backend/rest/repositories/SoDRepositoryBackend/SoDRepositoryBackend";
import AuthRepositoryBackend from "@/details/data/backend/rest/repositories/AuthRepositoryBackend/AuthRepositoryBackend";
import FakeNotificationsRepositoryBackend from "@/details/data/backend/rest/mock/FakeNotificationsRepositoryBackend";
import L1ReviewRepositoryBackend from "@/details/data/backend/rest/repositories/L1ReviewRepositoryBackend/L1ReviewRepositoryBackend";
import MatchReviewRepositoryBackend from "@/details/data/backend/rest/repositories/MatchReviewRepositoryBackend/MatchReviewRepositoryBackend";
import RiskFactorsRepositoryBackend from "@/details/data/backend/rest/repositories/RiskFactorsRepositoryBackend/RiskFactorsRepositoryBackend";
import P4ValidationRepositoryBackend from "@/details/data/backend/rest/repositories/P4ValidationRepositoryBackend/P4ValidationRepositoryBackend";
import KybRepositoryBackend from "@/details/data/backend/rest/repositories/KybRepositoryBackend/KybRepositoryBackend";
import KybCaseRepositoryBackend from "@/details/data/backend/rest/repositories/KybCaseRepositoryBackend/KybCaseRepositoryBackend";
import KybMandateRepositoryBackend from "@/details/data/backend/rest/repositories/KybMandateRepositoryBackend/KybMandateRepositoryBackend";
import CorporateFirstLoginRepositoryBackend from "@/details/data/backend/rest/repositories/CorporateFirstLoginRepositoryBackend/CorporateFirstLoginRepositoryBackend";
import KybSignatureGridRepositoryBackend from "@/details/data/backend/rest/repositories/KybSignatureGridRepositoryBackend/KybSignatureGridRepositoryBackend";
import KybCorporateAccessRepositoryBackend from "@/details/data/backend/rest/repositories/KybCorporateAccessRepositoryBackend/KybCorporateAccessRepositoryBackend";
import KybMonitoringRepositoryBackend from "@/details/data/backend/rest/repositories/KybMonitoringRepositoryBackend/KybMonitoringRepositoryBackend";
import KybRetentionRepositoryBackend from "@/details/data/backend/rest/repositories/KybRetentionRepositoryBackend/KybRetentionRepositoryBackend";
import KybPerimeterRepositoryBackend from "@/details/data/backend/rest/repositories/KybPerimeterRepositoryBackend/KybPerimeterRepositoryBackend";
import KybEvidencePackRepositoryBackend from "@/details/data/backend/rest/repositories/KybEvidencePackRepositoryBackend/KybEvidencePackRepositoryBackend";
import KybAccessRecertificationRepositoryBackend from "@/details/data/backend/rest/repositories/KybAccessRecertificationRepositoryBackend/KybAccessRecertificationRepositoryBackend";
import KybModulePolicyRepositoryBackend from "@/details/data/backend/rest/repositories/KybModulePolicyRepositoryBackend/KybModulePolicyRepositoryBackend";
import IdentityPolicyRepositoryBackend from "@/details/data/backend/rest/repositories/IdentityPolicyRepositoryBackend/IdentityPolicyRepositoryBackend";
import ScreeningPolicyRepositoryBackend from "@/details/data/backend/rest/repositories/ScreeningPolicyRepositoryBackend/ScreeningPolicyRepositoryBackend";
import MandatesRepositoryBackend from "@/details/data/backend/rest/repositories/MandatesRepositoryBackend/MandatesRepositoryBackend";
import SecretEncryptionRepositoryBackend from "@/details/data/backend/rest/repositories/SecretEncryptionRepositoryBackend/SecretEncryptionRepositoryBackend";
import CustomerPurgeRepositoryBackend from "@/details/data/backend/rest/repositories/CustomerPurgeRepositoryBackend/CustomerPurgeRepositoryBackend";

/**
 * Centralized Service Container for dependency injection
 * Manages all repositories in the application
 * Provides a single source of truth for all dependencies
 */
export class ServiceContainer {
  private static instance: ServiceContainer;
  private readonly httpClientFactory: HttpClientFactory;
  private readonly repositories: Map<string, unknown> = new Map();

  private constructor() {
    this.httpClientFactory = HttpClientFactory.getInstance();
    this.initializeRepositories();
  }

  public static getInstance(): ServiceContainer {
    if (!ServiceContainer.instance) {
      ServiceContainer.instance = new ServiceContainer();
    }
    return ServiceContainer.instance;
  }

  /**
   * Initialize all repositories with their backend implementations
   */
  private initializeRepositories(): void {
    const httpClient = this.httpClientFactory.getHttpClient();
    // Single switch (VITE_USE_MOCK_COMPLIANCE) flips composite verdict, EDD
    // 4-yeux, L1 Review and P4 Validation between the in-memory fakes and the
    // real HTTP backends together. Scoring explain has its own switch.
    const useComplianceMock = isComplianceMockEnabled();
    const useScoringMock = isScoringMockEnabled();

    // One binding per repository. The mapped type forces every RepositoryRegistry
    // key to have exactly one factory — a missing or extra key is a compile
    // error, so the interface list and the instantiation list cannot drift.
    const factories: {
      [K in keyof RepositoryRegistry]: () => RepositoryRegistry[K];
    } = {
      auth: () => new AuthRepositoryBackend(httpClient),
      corporateFirstLogin: () => new CorporateFirstLoginRepositoryBackend(httpClient),
      customers: () => new CustomersRepositoryBackend(httpClient),
      customerProfil: () => new CustomerProfilRepositoryBackend(httpClient),
      client: () => new ClientRepositoryBackend(httpClient),
      requests: () => new RequestsRepositoryBackend(httpClient),
      screening: () => new ScreeningRepositoryBackend(httpClient),
      prospects: () => new ProspectsRepositoryBackend(httpClient),
      users: () => new UsersRepositoryBackend(httpClient),
      roles: () => new RolesRepositoryBackend(httpClient),
      otp: () => new OTPRepositoryBackend(httpClient),
      contactVerification: () =>
        new ContactVerificationRepositoryBackend(httpClient),
      identity: () => new IdentityRepositoryBackend(httpClient),
      documentUpload: () => new DocumentUploadRepositoryBackend(httpClient),
      customerAnalysis: () =>
        new CustomerAnalysisRepositoryBackend(
          new FetchHttpRequest<FetchRequestOptions>(),
        ),
      parameters: () => new ParametersRepositoryBackend(httpClient),
      // Notifications — swap to NotificationsRepositoryBackend(httpClient) when API is ready.
      notifications: () => new FakeNotificationsRepositoryBackend(),
      language: () => new LocalStorageLanguageRepository(),
      navCollapsed: () => new LocalStorageNavCollapsedRepository(),
      metadata: () => new MetadataRepositoryBackend(httpClient),
      productsAccounts: () => new ProductsAccountsRepositoryBackend(httpClient),
      requiredDocuments: () =>
        new RequiredDocumentsRepositoryBackend(httpClient),
      screeningReports: () => new ScreeningReportsRepositoryBackend(httpClient),
      accounts: () => new AccountsRepositoryBackend(httpClient),
      miniStatement: () => new MiniStatementRepositoryBackend(httpClient),
      customerKYC: () => new CustomerKYCRepositoryBackend(httpClient),
      customerDocuments: () =>
        new CustomerDocumentsRepositoryBackend(httpClient),
      dashboardMetrics: () => new DashboardMetricsRepositoryBackend(httpClient),
      // RBAC rules — swap to RulesRepositoryBackend(httpClient) when API is ready.
      rules: () => new FakeRulesRepositoryBackend(),
      riskEntityReferential: () =>
        new RiskEntityReferentialRepositoryBackend(httpClient),
      agences: () => new AgencesRepositoryBackend(httpClient),
      riskScoringCompositor: () =>
        new RiskScoringCompositorRepositoryBackend(httpClient),
      scoringExplain: () =>
        useScoringMock
          ? new FakeScoringExplainRepositoryBackend()
          : new ScoringExplainRepositoryBackend(httpClient),
      compositeVerdict: () =>
        useComplianceMock
          ? new FakeCompositeVerdictRepositoryBackend()
          : new CompositeVerdictRepositoryBackend(httpClient),
      edd: () =>
        useComplianceMock
          ? new FakeEddRepositoryBackend()
          : new EddRepositoryBackend(httpClient),
      l1Review: () =>
        useComplianceMock
          ? new FakeL1ReviewRepositoryBackend()
          : new L1ReviewRepositoryBackend(httpClient),
      p4Validation: () =>
        useComplianceMock
          ? new FakeP4ValidationRepositoryBackend()
          : new P4ValidationRepositoryBackend(httpClient),
      matchReview: () => new MatchReviewRepositoryBackend(httpClient),
      riskFactors: () => new RiskFactorsRepositoryBackend(httpClient),
      sod: () => new SoDRepositoryBackend(httpClient),
      permissions: () => new PermissionsRepositoryBackend(httpClient),
      roleOwner: () => new RoleOwnerRepositoryBackend(httpClient),
      rbacMatrix: () => new RBACMatrixRepositoryBackend(httpClient),
      kyb: () => new KybRepositoryBackend(httpClient),
      kybCase: () => new KybCaseRepositoryBackend(httpClient),
      kybMandate: () => new KybMandateRepositoryBackend(httpClient),
      kybCorporateAccess: () => new KybCorporateAccessRepositoryBackend(httpClient),
      kybMonitoring: () => new KybMonitoringRepositoryBackend(httpClient),
      kybRetention: () => new KybRetentionRepositoryBackend(httpClient),
      kybPerimeter: () => new KybPerimeterRepositoryBackend(httpClient),
      kybEvidencePack: () => new KybEvidencePackRepositoryBackend(httpClient),
      kybAccessRecertification: () => new KybAccessRecertificationRepositoryBackend(httpClient),
      kybSignatureGrid: () => new KybSignatureGridRepositoryBackend(httpClient),
      kybModulePolicy: () => new KybModulePolicyRepositoryBackend(httpClient),
      identityPolicy: () => new IdentityPolicyRepositoryBackend(httpClient),
      screeningPolicyAdmin: () =>
        new ScreeningPolicyRepositoryBackend(httpClient),
      mandates: () => new MandatesRepositoryBackend(httpClient),
      secretEncryption: () => new SecretEncryptionRepositoryBackend(httpClient),
      customerPurge: () => new CustomerPurgeRepositoryBackend(httpClient),
    };

    // Object.entries (not factories[key]) keeps every access static — no dynamic
    // object indexing, which the semgrep detect-object-injection rule blocks.
    for (const [key, factory] of Object.entries(factories)) {
      this.repositories.set(key, factory());
    }
  }

  /**
   * Get a repository by key. The key is checked against RepositoryRegistry and
   * the return type is inferred — a typo'd key is a compile error.
   */
  public getRepository<K extends keyof RepositoryRegistry>(
    key: K,
  ): RepositoryRegistry[K] {
    const repository = this.repositories.get(key);
    if (!repository) {
      throw new Error(`Repository with key '${key}' not found`);
    }
    return repository as RepositoryRegistry[K];
  }

  /**
   * Get the HTTP client factory
   */
  public getHttpClientFactory(): HttpClientFactory {
    return this.httpClientFactory;
  }

  /**
   * Get the HTTP client instance
   */
  public getHttpClient(): HttpClient {
    return this.httpClientFactory.getHttpClient();
  }

  /**
   * Register a custom repository (useful for testing or special cases)
   */
  public registerRepository<T>(key: string, repository: T): void {
    this.repositories.set(key, repository);
  }

  /**
   * Get all available repository keys
   */
  public getRepositoryKeys(): string[] {
    return Array.from(this.repositories.keys());
  }

  /**
   * Reset the container (useful for testing)
   */
  public reset(): void {
    this.repositories.clear();
    this.httpClientFactory.reset();
    this.initializeRepositories();
  }
}

export default ServiceContainer;
